import { makeStyles, tokens, Title2, Body1, Caption1, Button } from '@fluentui/react-components';
import { PlugDisconnectedRegular } from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { useAccess } from './AccessContext';

/* Shown when the user IS signed in but we couldn't resolve their access because of a
   transient/infrastructure failure — the access token couldn't be acquired, or /api/me
   returned a 502/5xx/network/401 error. This is deliberately NOT the "no access" screen:
   an infra hiccup must never read as "you're denied" (SPA-Auth Standard, rule 5). The
   primary action is Retry; the diagnostic shows what the app actually saw (rule 7). */

const useStyles = makeStyles({
  page: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
    maxWidth: '32rem', // readable column width; no spacing token this large
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
  },
  icon: {
    fontSize: '48px', // glyph size, not layout spacing
    color: tokens.colorPaletteYellowForeground1,
  },
  message: { color: tokens.colorNeutralForeground2 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalM },
  diag: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    maxWidth: '32rem',
    textAlign: 'left',
    overflowWrap: 'anywhere',
  },
});

export function ServiceErrorPage() {
  const styles = useStyles();
  const { displayName, logoutRedirect } = useAuth();
  const { diagnostics, retry } = useAccess();

  return (
    <div className={styles.page}>
      <main className={styles.card} role="alert" aria-live="polite" data-testid="service-error">
        <PlugDisconnectedRegular className={styles.icon} aria-hidden />
        <Title2>We can&apos;t reach DevOps Reports</Title2>
        <Body1 className={styles.message}>
          {displayName ? (
            <>
              You&apos;re signed in as <strong>{displayName}</strong>, but we couldn&apos;t load your
              workspace just now. This is usually temporary — please try again.
            </>
          ) : (
            <>We couldn&apos;t load your workspace just now. This is usually temporary — please try again.</>
          )}
        </Body1>
        <div className={styles.actions}>
          <Button appearance="primary" data-testid="retry" onClick={retry}>
            Try Again
          </Button>
          <Button appearance="secondary" data-testid="sign-out" onClick={() => void logoutRedirect()}>
            Sign Out
          </Button>
        </div>
        <Caption1 className={styles.diag} data-testid="service-error-diagnostics">
          diagnostics — account: {diagnostics.email ?? '(none)'}
          {diagnostics.profileStatus ? ` · /me: HTTP ${diagnostics.profileStatus}` : ''}
          {diagnostics.profileError ? ` · ${diagnostics.profileError}` : ''}
          {diagnostics.tokenError ? ` · token: ${diagnostics.tokenError}` : ''}
        </Caption1>
      </main>
    </div>
  );
}
